import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import { defaultSchema } from "hast-util-sanitize";
import {
  TimelineWrapper,
  DateWrapper,
  MessageWrapper,
  CollapsableWrapper,
  ErrorMessage,
  DateSenderWrapper,
} from "./TimelineBox.styles";
import Icon from "../../../../../elements/icon/Icon";
import { useStore } from "../../../../../hooks";
import Spinner from "../../../../../components/common/Spinner";
import { AGENT_ROLES } from "../../../../../utils/enums";
import { observer } from "mobx-react";
import { MarkdownStyles } from '../../../../../elements/ChatBox/MarkdownStyles';
import { highlightMatches } from "../../../../../utils/highlightHelper";

const groupMessagesByDate = (messages) => {
  const grouped = {};
  messages.forEach((msg) => {
    const date = dayjs(msg.insert_date).format("YYYY-MM-DD");
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(msg);
  });
  return grouped;
};

const getInitials = (name) => name.charAt(0).toUpperCase();

const extendedSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "span", "div"],
  attributes: {
    ...defaultSchema.attributes,
    '*': [
      ...(defaultSchema.attributes?.['*'] || []),
      'className',
      'class',
      'style',
      'data-highlight-index',
      'id'
    ],
    span: [
      ...(defaultSchema.attributes?.span || []),
      'className',
      'class',
      'style',
      'data-highlight-index',
      'id'
    ],
    div: [
      ...(defaultSchema.attributes?.div || []),
      'className',
      'class',
      'style',
      'data-highlight-index',
      'id'
    ]
  }
};

const TimelineBox = observer(({ matches = [] }) => {
  const [collapsed, setCollapsed] = useState({});
  const [localTimelineMessages, setLocalTimelineMessages] = useState([]);
  const containerRef = useRef(null);
  const { assistanceUCPStore } = useStore();
  const { timelineMessages, isCustomerMessagesError, isLoadingMessages } = assistanceUCPStore;

  useEffect(() => {
    if (timelineMessages && timelineMessages.length > 0) {
      const initialCollapsedState = timelineMessages.reduce((acc, msg) => {
        if (msg.channel?.toLowerCase() === "email") {
          acc[msg.id] = true;
        }
        return acc;
      }, {});
      
      setCollapsed(initialCollapsedState);
      
      setLocalTimelineMessages([...timelineMessages]);

      setTimeout(() => {
        if (containerRef.current) {
          const lastMessage = containerRef.current.lastElementChild;
          if (lastMessage) {
            lastMessage.scrollIntoView({ block: "end" });
          }
        }
      }, 100);
    } else if (timelineMessages && timelineMessages.length === 0) {
      setLocalTimelineMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineMessages]);

  const groupedMessages = useMemo(() => groupMessagesByDate(localTimelineMessages), [localTimelineMessages]);

  const toggleCollapse = useCallback((id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isHTML = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };

  const insertHighlights = (text, matches, messageId) => {
    if (!matches || matches.length === 0) return text;

    const sorted = [...matches].sort((a, b) => b.start_at - a.start_at);
    
    let result = text;
    sorted.forEach(({ start_at, end_at }, index) => {
      const before = result.slice(0, start_at);
      const match = result.slice(start_at, end_at);
      const after = result.slice(end_at);
      result = `${before}<span id="highlight_${messageId}_${index}" class="highlighted-text" data-highlight-index="${index}" style="background-color: #FFEB3B; display: inline; padding: 0 2px; border-radius: 2px;">${match}</span>${after}`;
    });

    return result;
  };

  const renderContent = (message, matches = [], messageId) => {
    // Type validation to prevent rendering objects
    let messageContent = message;
    
    if (typeof messageContent !== 'string') {
      console.error('Message is not a string:', messageContent);
      // Try to extract string from object
      if (typeof messageContent === 'object' && messageContent !== null) {
        if (messageContent.message) messageContent = messageContent.message;
        else if (messageContent.content) messageContent = messageContent.content;
        else if (messageContent.text) messageContent = messageContent.text;
        else {
          return (
            <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
              Error rendering message: Invalid message format
            </div>
          );
        }
      } else {
        return <div style={{ color: 'red' }}>Unable to display message</div>;
      }
    }

    if (isHTML(messageContent)) {
      const highlighted = highlightMatches(messageContent, matches, messageId);
      return (
        <div className="html-content message-scope">
          {highlighted}
        </div>
      );
    }

    const highlightedText = insertHighlights(messageContent, matches, messageId);

    return (
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkParse,
          [remarkRehype, {
            singleTilde: true,
            stringLength: true,
            tablePipeAlign: true,
            tableCellPadding: true
          }]
        ]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, extendedSchema],
          rehypeStringify
        ]}
        components={{
          table: ({node, ...props}) => (
            <table className="markdown-table" {...props} />
          ),
          thead: ({node, ...props}) => (
            <thead className="markdown-thead" {...props} />
          ),
          tbody: ({node, ...props}) => (
            <tbody className="markdown-tbody" {...props} />
          ),
          tr: ({node, ...props}) => (
            <tr className="markdown-tr" {...props} />
          ),
          th: ({node, ...props}) => (
            <th className="markdown-th" {...props} />
          ),
          td: ({node, ...props}) => (
            <td className="markdown-td" {...props} />
          ),
          a: ({ node, ...props }) => (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a 
              className="gfm-link" 
              target="_blank" 
              rel="noopener noreferrer" 
              {...props} 
            />
          ),
          div: ({node, ...props}) => <div {...props} />,
          span: ({node, className, ...props}) => {
            const spanProps = {
              className: className || props.class,
              id: props.id,
              'data-highlight-index': props['data-highlight-index'],
              style: props.style
            };
            return <span {...spanProps}>{props.children}</span>;
          },
          img: ({node, ...props}) => <img {...props} alt={props.alt || ''} />,
          p: ({node, ...props}) => <p {...props} />,
          br: ({node, ...props}) => <br {...props} />,
          hr: ({node, ...props}) => <hr {...props} />
        }}
        className="message-scope"
      >
        {highlightedText}
      </ReactMarkdown>
    );
  };

  return (
    <>
      <MarkdownStyles />
      <TimelineWrapper ref={containerRef}>
        {isCustomerMessagesError ? (
          <ErrorMessage>
            <p>Unable to fetch conversation history at the moment. Please try again later.</p>
          </ErrorMessage>
        ) : isLoadingMessages ? (
          <ErrorMessage>
            <Spinner size="large" />
          </ErrorMessage>
        ) : localTimelineMessages.length === 0 ? (
          <ErrorMessage>
            <p>No messages found.</p>
          </ErrorMessage>
        ) : (
          <>
            {Object.keys(groupedMessages).map((date) => (
              <div key={date}>
                <DateWrapper>
                  <span>{dayjs(date).format("MM/DD/YYYY")}</span>
                </DateWrapper>
                {groupedMessages[date].map((msg) => {
                  const isAgent = AGENT_ROLES.some(role => msg.sender?.toLowerCase().includes(role.toLowerCase()));
                  const isEmail = msg.channel?.toLowerCase() === "email";
                  const isAnswerAgent = msg.role?.toLowerCase().includes("agent");

                  const messageWrapperClasses = classNames({
                    'message-container': true,
                    'agent-message': isAgent,
                    'user-message': !isAgent,
                    'email-message': isEmail
                  });

                  const avatarClasses = classNames('assistant-avatar', {
                    'email-mode': isEmail
                  });

                  return (
                    <MessageWrapper
                      key={msg.id}
                      isAgent={isAgent}
                      isAnswerAgent={isAnswerAgent}
                      isEmail={isEmail}
                      className={messageWrapperClasses}
                    >
                      {isAgent && (
                        <div
                          className={avatarClasses}
                          data-tooltip={msg.channel}
                        >
                          A
                        </div>
                      )}
                      {isAgent && isEmail ? (
                        <CollapsableWrapper>
                          <div
                            className={classNames('email-header', {
                              'expanded': collapsed[msg.id]
                            })}
                            data-tooltip={msg.channel}
                            onClick={() => toggleCollapse(msg.id)}
                          >
                            <div
                              className={classNames('sender-info', {
                                'with-tooltip': true
                              })}
                              data-tooltip={msg.channel}
                            >
                              <Icon
                                name="email"
                                height="15px"
                                width="15px"
                                className="icon"
                              />
                              <span className="sender-email">
                                {`<${msg.sender}>`}
                              </span>
                            </div>

                            <DateSenderWrapper>
                              <span className="email-date">
                                {dayjs(msg.insert_date).format("MM/DD/YYYY")}
                              </span>
                              <button
                                className={classNames('collapse-button', {
                                  'expanded': collapsed[msg.id]
                                })}
                                aria-expanded={!collapsed[msg.id]}
                                aria-controls={`email-body-${msg.id}`}
                              >
                                <Icon
                                  name={collapsed[msg.id] ? "arrowUp" : "expandMoreOutlinedBlack"}
                                  width="9px"
                                  height="9px"
                                  margin="0 12px"
                                />
                              </button>
                            </DateSenderWrapper>
                          </div>

                          <div
                            className={classNames('email-body', {
                              'collapsed': !collapsed[msg.id],
                              'message-scope': true
                            })}
                            id={`email-body-${msg.id}`}
                          >
                            <strong>RE: {msg.subject || ""}</strong>
                            <div className="markdown-content message-scope">
                              {renderContent(msg.message, msg.matches, msg.id)}
                            </div>
                          </div>
                        </CollapsableWrapper>
                      ) : (
                        <div className={classNames('message-content', {
                          'agent': isAgent,
                          'user': !isAgent,
                          'message-scope': true
                        })}>
                          <div className="markdown-content message-scope">
                            {renderContent(msg.message, msg.matches, msg.id)}
                          </div>
                        </div>
                      )}
                      {!isAgent && (
                        <div className={classNames('avatar', 'user-avatar')}>
                          {getInitials(msg.sender || '')}
                        </div>
                      )}
                    </MessageWrapper>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </TimelineWrapper>
    </>
  );
});

export default TimelineBox;
